import { FormValue } from '@coach-bot/data-access/shared/interfaces';
import { StudentCurrencyTransactionEventPayload, StudentEvent } from '@coach-bot/data-access/shared/models';
import { isRestrictedEmail } from '@coach-bot/data-access/shared/utils/permissions';
import {
  EXTENSION_NAME_LEARN_AND_EARN_FRIENDLY,
  EXTENSION_NAME_LEARN_AND_EARN_OFFICIAL,
  EXTENSION_NAME_LEARN_WITH_AI_FRIENDLY,
  EXTENSION_NAME_LEARN_WITH_AI_OFFICIAL,
} from '@shared/extension/constants';
import { captureSentryMessage } from '@shared/sentry';
import { getRawTranslation } from '@shared/translate/utils/get-raw-translation';
import { ONE_MINUTE_MS, ONE_SECOND_MS } from '@shared/utils';
import { Subscription, combineLatest, filter, interval, take, timer } from 'rxjs';
import { processReward, showNotification } from '../utils';
import { Services } from './services.service';

const STUDENT_CURRENCY_TRANSACTION_EVENT_TYPE = 'student_currency_transaction';

const CLEAR_FORM_VALUE_TIMER_MS = ONE_MINUTE_MS * 30;
const CHECK_IS_EXTENSION_POPUP_OPENED_INTERVAL_MS: number = ONE_SECOND_MS * 15;
const UPDATE_LAST_LEARNING_ACTIVITY_TIME_INTERVAL_MS: number = ONE_SECOND_MS * 30;

export class ServiceWorkerSubscriptionsService {
  private checkIsExtensionPopupOpenedIntervalSubscription: Subscription | null;
  private clearForgotPasswordFormValueTimerSubscription: Subscription | null;
  private clearSignInFormValueTimerSubscription: Subscription | null;
  private commonStoreInitializedSubscription: Subscription | null;
  private currentUrlAndThirdPartyAppCredentialsListSubscription: Subscription | null;
  private extensionIconSubscription: Subscription | null;
  private extensionNameSubscription: Subscription | null;
  private forgotPasswordFormValueSubscription: Subscription | null;
  private isLearningAnyRecommendationSubscription: Subscription | null;
  private isSignedInSubscription: Subscription | null;
  private moneyRewardSubscription: Subscription | null;
  private profileEmailSubscription: Subscription | null;
  private showLearningReminderSubscription: Subscription | null;
  private signInFormValueSubscription: Subscription | null;
  private studentEventsSubscription: Subscription | null;
  private updateLastLearningActivityTimeIntervalSubscription: Subscription | null;

  private subscriptions: Subscription[] = [];

  constructor(private services: Services) {}

  subscribe(): void {
    if (this.subscriptions.length > 0) {
      return;
    }

    this.commonStoreInitializedSubscription = this.services.commonStore.initialized$
      .pipe(
        filter((item: boolean) => item),
        take(1)
      )
      .subscribe(() => {
        this.handleCommonStoreInitialized();
      });

    this.extensionIconSubscription = this.services.studentWatchService.extensionIcon$.subscribe((icon: string) => {
      this.services.contextMenuService.setExtensionIcon(icon);
      this.services.coachBotViewStore.setExtensionIcon(icon.split('/').slice(1).join('/'));
    });

    this.isLearningAnyRecommendationSubscription = this.services.studentStore.isLearningAnyRecommendation$.subscribe(
      (isLearningAnyRecommendation: boolean) => {
        if (isLearningAnyRecommendation) {
          this.setLastLearningAcitivityTime();
          this.subscribeToUpdateLastLearningActivityTime();
        } else {
          this.unsubscribeFromUpdateLastLearningActivityTime();
        }
      }
    );

    this.isSignedInSubscription = this.services.commonStore.isSignedIn$.subscribe((isSignedIn: boolean) => {
      if (isSignedIn) {
        this.subscribeToStudentEvents();
      } else {
        this.unsubscribeFromStudentEvents();
      }
    });

    this.moneyRewardSubscription = this.services.studentStore.moneyReward$
      .pipe(filter((moneyReward: number) => moneyReward > 0))
      .subscribe(async (moneyReward: number) => {
        this.services.studentStore.setMoneyReward(null);
        processReward(moneyReward);
      });

    this.forgotPasswordFormValueSubscription = this.services.coachBotUserStore.forgotPasswordFormValue$.subscribe(
      (value: FormValue | null) => {
        if (value) {
          if (this.clearForgotPasswordFormValueTimerSubscription) {
            this.clearForgotPasswordFormValueTimerSubscription.unsubscribe();
          }
          this.clearForgotPasswordFormValueTimerSubscription = timer(CLEAR_FORM_VALUE_TIMER_MS).subscribe(() => {
            this.services.coachBotUserStore.setForgotPasswordFormValue(null);
          });
        }
      }
    );

    this.profileEmailSubscription = this.services.commonStore.profileEmail$.subscribe((email: string) => {
      const isUserRestricted: boolean = isRestrictedEmail(email);
      this.services.coachBotViewStore.setExtensionNameFriendly(
        isUserRestricted ? EXTENSION_NAME_LEARN_WITH_AI_FRIENDLY : EXTENSION_NAME_LEARN_AND_EARN_FRIENDLY
      );
      const extensionNameOfficial: string = isUserRestricted
        ? EXTENSION_NAME_LEARN_WITH_AI_OFFICIAL
        : EXTENSION_NAME_LEARN_AND_EARN_OFFICIAL;
      this.services.coachBotViewStore.setExtensionNameOfficial(extensionNameOfficial);
      this.services.contextMenuService.setExtensionName(extensionNameOfficial);
    });

    this.signInFormValueSubscription = this.services.coachBotUserStore.signInFormValue$.subscribe(
      (value: FormValue | null) => {
        if (value) {
          if (this.clearSignInFormValueTimerSubscription) {
            this.clearSignInFormValueTimerSubscription.unsubscribe();
          }
          this.clearSignInFormValueTimerSubscription = timer(CLEAR_FORM_VALUE_TIMER_MS).subscribe(() => {
            this.services.coachBotUserStore.setSignInFormValue(null);
          });
        }
      }
    );

    this.showLearningReminderSubscription = this.services.studentWatchService.showLearningReminder$.subscribe(
      async () => {
        showNotification(
          await getRawTranslation('extension.learning_reminder.labels.title'),
          await getRawTranslation('extension.learning_reminder.labels.message')
        );
      }
    );

    this.resetCheckIsExtensionPopupOpenedInterval();

    this.subscriptions.push(
      this.commonStoreInitializedSubscription,
      this.extensionIconSubscription,
      this.extensionNameSubscription,
      this.forgotPasswordFormValueSubscription,
      this.isLearningAnyRecommendationSubscription,
      this.isSignedInSubscription,
      this.moneyRewardSubscription,
      this.profileEmailSubscription,
      this.showLearningReminderSubscription,
      this.signInFormValueSubscription
    );
    captureSentryMessage('Subscribed to service worker events');
  }

  unsubscribe(): void {
    captureSentryMessage('Unsubscribing from service worker events');
    if (this.clearForgotPasswordFormValueTimerSubscription) {
      this.clearForgotPasswordFormValueTimerSubscription.unsubscribe();
      this.clearForgotPasswordFormValueTimerSubscription = null;
    }
    if (this.clearSignInFormValueTimerSubscription) {
      this.clearSignInFormValueTimerSubscription.unsubscribe();
      this.clearSignInFormValueTimerSubscription = null;
    }
    if (this.checkIsExtensionPopupOpenedIntervalSubscription) {
      this.checkIsExtensionPopupOpenedIntervalSubscription.unsubscribe();
      this.checkIsExtensionPopupOpenedIntervalSubscription = null;
    }
    this.unsubscribeFromStudentEvents();
    this.unsubscribeFromUpdateLastLearningActivityTime();
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    this.subscriptions = [];
  }

  resetCheckIsExtensionPopupOpenedInterval(): void {
    if (this.subscriptions.length === 0) {
      return;
    }
    if (this.checkIsExtensionPopupOpenedIntervalSubscription) {
      this.checkIsExtensionPopupOpenedIntervalSubscription.unsubscribe();
    }
    this.checkIsExtensionPopupOpenedIntervalSubscription = timer(CHECK_IS_EXTENSION_POPUP_OPENED_INTERVAL_MS).subscribe(
      () => {
        this.services.commonStore.setIsExtensionPopupOpened(false);
      }
    );
  }

  private handleCommonStoreInitialized(): void {
    if (this.services.commonStore.userId$.value) {
      this.services.authService.signOut();
    } else {
      this.services.contextMenuService.updateContextMenuActions();
    }

    this.currentUrlAndThirdPartyAppCredentialsListSubscription = combineLatest([
      this.services.studentStore.currentUrl$,
      this.services.studentStore.thirdPartyAppCredentialsList$,
    ]).subscribe(() => {
      this.services.contextMenuService.updateContextMenuActions();
    });

    this.subscriptions.push(this.currentUrlAndThirdPartyAppCredentialsListSubscription);
  }

  private subscribeToStudentEvents(): void {
    if (!this.services.commonStore.studentId$.value) {
      console.error('studentId is not defined to subscribe to student messages');
      return;
    }
    if (this.studentEventsSubscription) {
      this.studentEventsSubscription.unsubscribe();
    }
    this.studentEventsSubscription = this.services.studentState
      .subscribeToStudentEvents(this.services.commonStore.studentId$.value)
      .subscribe((event: StudentEvent) => {
        if (
          event?.onPostLearningAppMessage?.payload &&
          event.onPostLearningAppMessage.messageType === STUDENT_CURRENCY_TRANSACTION_EVENT_TYPE
        ) {
          const payload: StudentCurrencyTransactionEventPayload = JSON.parse(event.onPostLearningAppMessage.payload);
          if (typeof payload?.balance === 'number') {
            this.services.studentState.handleNewStudentBalance(Number((payload.balance / 10).toFixed(2)));
          }
        }
      });
  }

  private unsubscribeFromStudentEvents(): void {
    if (this.studentEventsSubscription) {
      this.studentEventsSubscription.unsubscribe();
      this.studentEventsSubscription = null;
    }
  }

  private setLastLearningAcitivityTime(): void {
    this.services.studentStore.setLastLearningActivityTime(Date.now());
    if (this.services.studentStore.learningReminderDisplayTimes$.value) {
      this.services.studentStore.setLearningReminderDisplayTimes(null);
    }
  }

  private subscribeToUpdateLastLearningActivityTime(): void {
    if (!this.updateLastLearningActivityTimeIntervalSubscription) {
      this.updateLastLearningActivityTimeIntervalSubscription = interval(
        UPDATE_LAST_LEARNING_ACTIVITY_TIME_INTERVAL_MS
      ).subscribe(() => {
        this.setLastLearningAcitivityTime();
      });
    }
  }

  private unsubscribeFromUpdateLastLearningActivityTime(): void {
    if (this.updateLastLearningActivityTimeIntervalSubscription) {
      this.updateLastLearningActivityTimeIntervalSubscription.unsubscribe();
      this.updateLastLearningActivityTimeIntervalSubscription = null;
    }
  }
}
